import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Avatar
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';

const AgentPerformanceTable = ({ data }) => {
  // Handle case where data might be the entire response object
  const processedData = Array.isArray(data) ? data : (data?.data || []);
  
  if (!processedData || processedData.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height={200}
        flexDirection="column"
      >
        <Typography variant="body2" color="text.secondary" gutterBottom>
          No agent performance data available
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Agent performance will appear here once you have call data
        </Typography>
      </Box>
    );
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  const getSuccessRateIcon = (rate, previousRate) => {
    if (!previousRate) return null;
    if (rate > previousRate) return <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />;
    if (rate < previousRate) return <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    return null;
  };

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Agent</TableCell>
            <TableCell align="right">Calls</TableCell>
            <TableCell align="right">Completed</TableCell>
            <TableCell align="right">Avg Duration</TableCell>
            <TableCell align="right">Success Rate</TableCell>
            <TableCell align="right">Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processedData.map((agent, index) => (
            <TableRow key={agent.agentId || index} hover>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: agent.agentStatus ? 'success.light' : 'grey.light'
                    }}
                  >
                    {agent.agentName ? agent.agentName.charAt(0).toUpperCase() : 'A'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {agent.agentName || 'Unknown Agent'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {agent.agentId}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              
              <TableCell align="right">
                <Typography variant="body2" fontWeight="medium">
                  {agent.calls || 0}
                </Typography>
              </TableCell>
              
              <TableCell align="right">
                <Typography variant="body2">
                  {agent.completedCalls || 0}
                </Typography>
              </TableCell>
              
              <TableCell align="right">
                <Typography variant="body2">
                  {formatDuration(agent.averageDuration || 0)}
                </Typography>
              </TableCell>
              
              <TableCell align="right">
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                  {getSuccessRateIcon(agent.successRate, agent.previousSuccessRate)}
                  <Chip
                    label={`${agent.successRate || 0}%`}
                    size="small"
                    color={getSuccessRateColor(agent.successRate || 0)}
                    variant="outlined"
                  />
                </Box>
              </TableCell>
              
              <TableCell align="right">
                <Chip
                  label={agent.agentStatus ? 'Active' : 'Inactive'}
                  size="small"
                  color={agent.agentStatus ? 'success' : 'default'}
                  variant="outlined"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AgentPerformanceTable; 